import { React, useState, useEffect, useCallback, useMemo } from 'react';
import debounce from "lodash.debounce";
import { useTranslation } from "react-i18next";
import { motion } from 'framer-motion';
import "./index.scss";

import SideModal from '../../components/ui/SideModal';
import Pagination from "../../components/ui/Pagination";
import NoDataFound from "../../components/ui/NoDataFound";
import SkeletonLoader from "../../components/ui/SkeletonLoader";

import SideNav from "../../components/PostLogin/SideNav";
import MonitorTileView from "../../components/PostLogin/Monitor/MonitorTileView";
import MonitorListView from "../../components/PostLogin/Monitor/MonitorListView";
import AddMonitorModal from '../../components/PostLogin/Monitor/AddMonitorModal';

import { tileViewIcon, listViewIcon, searchIcon } from "../../utils/icons.js";
import { formatNumber } from "../../utils/util";

import { useSelector, useDispatch } from 'react-redux';
import { getMonitorList, resetDeleteMonitorResponse, getMonitorStats, resetAddMonitorResponse } from '../../store/actions/monitorAction';

const Monitor = () => {

    const monitorStatsResponse = useSelector((state) => state.monitor?.monitorStatsResponse)
    const monitorListResponse = useSelector((state) => state.monitor?.monitorList);
    const deleteMonitorResponse = useSelector((state) => state.monitor?.deleteMonitorResponse);

    const addMonitorResponse = useSelector((state) => state.monitor?.addMonitorResponse);

    const { t } = useTranslation();

    const dispatch = useDispatch();

    const [viewType, setViewType] = useState('tile');
    const [showAddMonitorModal, setShowAddMonitorModal] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const [updatedSearchValue, setUpdatedSearchValue] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [currentPageSize, setCurrentPageSize] = useState(10);
    const [isLoading, setIsLoading] = useState(false);
    const [isStatsLoading, setIsStatsLoading] = useState(false);

    const closeAddOrgSidePanel = () => {
        setShowAddMonitorModal(false)
    }

    const backtoMainPage = () => {
        if(addMonitorResponse?.detail?.status === "success") {
            setIsLoading(true)
            setCurrentPage(1);
            setCurrentPageSize(10);
            fetchMonitorList();
            fetchMonitorStats();
        }
        dispatch(resetAddMonitorResponse())
        closeAddOrgSidePanel()
    }

    const searchMonitor = (e) => {
        setSearchValue(e.target.value);
        requestSearch(e.target.value)
        setCurrentPage(1);
    }

    const requestSearch = useMemo(() => debounce((searchTerm) => {        
        setUpdatedSearchValue(searchTerm)
    }, 500), []);

    const setCurrentPageandRecordSize = (currentPage, pageSize) => {
        setCurrentPage(currentPage);
        if(pageSize !== currentPageSize) {
            setCurrentPageSize(pageSize);
            setCurrentPage(1);
        }
    }

    const fetchMonitorList = useCallback(()=>{
        const baseUrl = `&page=${currentPage}&page_size=${currentPageSize}`;
        const queryParams = updatedSearchValue ? `&query=${updatedSearchValue}` : "";
        const url = `${baseUrl}${queryParams}`
        setIsLoading(true);
        dispatch(getMonitorList(url));
    },[updatedSearchValue, currentPage, currentPageSize, dispatch])

    const fetchMonitorStats = useCallback(()=>{
        setIsStatsLoading(true);
        dispatch(getMonitorStats());
    },[dispatch])

    useEffect(()=>{
        fetchMonitorStats();
    },[fetchMonitorStats])

    useEffect(() => {
        fetchMonitorList();        
    },[fetchMonitorList])

    useEffect(() => {
        if(monitorListResponse !== null) {
            setIsLoading(false);
        }
    },[monitorListResponse])

    useEffect(()=>{
        if(deleteMonitorResponse !== null) {
            if(deleteMonitorResponse?.detail.status === "success") { 
                fetchMonitorList();
                fetchMonitorStats();
            }
            dispatch(resetDeleteMonitorResponse())
        }
    },[deleteMonitorResponse, fetchMonitorList, fetchMonitorStats, dispatch]);

    useEffect(()=>{
        if(monitorStatsResponse !== null) {
            setIsStatsLoading(false);
        }
    },[monitorStatsResponse])


    return (
        <div className="users-container">
            <SideNav />
            <div className="monitor-page-container">
                <div className="page-header-container px-4 py-3 d-flex align-items-center justify-content-between">
                    <div className="header-left">
                        <h1>{t("monitor.heading")}</h1>
                    </div>
                    <div className="header-right">
                        <button onClick={() => { setShowAddMonitorModal(true) }}>{t("monitor.addMonitor")}</button>
                    </div>
                </div>
                <div className="page-search-header px-4 py-3 d-flex align-items-center justify-content-between">
                    <div className="left-search-container position-relative">
                        <span className="position-absolute search-icon">{searchIcon()}</span>
                        <input type='text' placeholder='Search Monitor' className="search-input" value={searchValue} onChange={searchMonitor}/>
                    </div>
                    <div className="right-view-option d-flex">
                        <div className={`tile-view-image ${viewType === 'tile' ? 'active' : ''}`} onClick={()=>setViewType('tile')}>
                            {tileViewIcon()}
                        </div>
                        <div className={`list-view-image ${viewType === 'list' ? 'active' : ''}`} onClick={()=>setViewType('list')}>
                            {listViewIcon()}
                        </div>
                    </div>
                </div>
                <div className="page-main-section p-4">
                    <div className="users-header-container p-3 my-3">
                        <div className="d-flex gap-3">
                            <div className="total-score-container rounded">
                                {
                                    isStatsLoading ?
                                    <SkeletonLoader type={"line"} count={2} currentPage={"monitor"}/>
                                    :
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.4 }}
                                    >
                                        <div className="users-totol-count d-flex">
                                            <p className="total-user-count">{formatNumber(monitorStatsResponse?.monitors?.total)}</p>
                                            <div className="user-count mx-3 mt-2">
                                            </div>
                                        </div>
                                        <div className="active-disabled-count d-flex gap-3">
                                            <p className="active-user"> {t("monitor.domainScan")}</p>
                                        </div>
                                    </motion.div>
                                }                                
                            </div>
                            <div className="total-score-container rounded">
                                {
                                    isStatsLoading ?
                                    <SkeletonLoader type={"line"} count={2} currentPage={"monitor"}/>
                                    :
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.4 }}
                                    >
                                        <div className="users-totol-count d-flex">
                                            <p className="total-user-count">{formatNumber( monitorStatsResponse?.monitors?.weekly)}</p>
                                            <div className="user-count mx-3 mt-2">
                                            </div>
                                        </div>
                                        <div className="active-disabled-count d-flex gap-3">
                                            <p className="active-user"> {t("monitor.weeklyScan")}</p>
                                        </div>
                                    </motion.div>
                                }                                
                            </div>
                            <div className="total-score-container rounded">
                                {
                                    isStatsLoading ?
                                    <SkeletonLoader type={"line"} count={2} currentPage={"monitor"}/>
                                    :
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.4 }}
                                    >
                                        <div className="users-totol-count d-flex">
                                            <p className="total-user-count">{formatNumber(monitorStatsResponse?.monitors?.monthly)}</p>
                                            <div className="user-count mx-3 mt-2">
                                            </div>
                                        </div>
                                        <div className="active-disabled-count d-flex gap-3">
                                            <p className="active-user"> {t("monitor.monthlyScan")}</p>
                                        </div>
                                    </motion.div>
                                }                                
                            </div>
                        </div>
                    </div>
                    <h2 className="secondary-heading my-4">{t("monitor.selectaDomain")}</h2>
                    {viewType === "tile" && <MonitorTileView monitorList={monitorListResponse} isLoading={isLoading} />}
                    {viewType === "list" && <MonitorListView monitorList={monitorListResponse} isLoading={isLoading} />}
                    {monitorListResponse != null && monitorListResponse?.results.length > 0 && <Pagination
                        className="pagination-bar"
                        currentPage={currentPage}
                        pageSize={currentPageSize}
                        totalCount={monitorListResponse.meta.total_results}
                        onPageChange={setCurrentPageandRecordSize}
                    />}
                    {
                        !isLoading && monitorListResponse?.meta?.total_results === 0 && 
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.4 }}
                        >
                            <NoDataFound />
                        </motion.div>
                        
                    }                    
                </div>
            </div>
            <SideModal showSidePanel={showAddMonitorModal} hideSidePanel={backtoMainPage}>
                {showAddMonitorModal && <AddMonitorModal closeAddmonitorModal={backtoMainPage}/>}
            </SideModal>
        </div>
    )
}

export default Monitor;